import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { mockData } from '../data/mock';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[rgba(0,0,0,0.1)] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <a
            href="/"
            className="flex items-center gap-2 text-[rgb(0,55,32)] hover:text-[rgb(13,121,22)] transition-colors font-medium"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-4xl font-bold text-[rgb(0,55,32)] mb-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-[rgb(131,146,140)] mb-8">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none space-y-8 text-[rgb(14,15,12)]">
          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">1. Introduction</h2>
            <p className="leading-relaxed">
              {mockData.company.name} ("we", "us", or "our") operates the website and is committed to
              protecting the privacy of our visitors and customers. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">2. Information We Collect</h2>
            <p className="leading-relaxed mb-3">We may collect the following types of personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Contact Information:</strong> Name, email address, phone number, and mailing address when you submit a form, request a consultation, or download our e-book.</li>
              <li><strong>Project Information:</strong> Details about your renovation project, budget, timeline, and property type that you voluntarily provide.</li>
              <li><strong>Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and referring pages.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device type used to access our site.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">3. How We Use Your Information</h2>
            <p className="leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Respond to your inquiries and consultation requests</li>
              <li>Send you the requested e-book or renovation guide</li>
              <li>Provide estimates and project-related communications</li>
              <li>Improve our website and services</li>
              <li>Send relevant updates about our services (only if you opt in)</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">4. Information Sharing</h2>
            <p className="leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share
              your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Service Providers:</strong> Trusted partners who assist us in operating our website and conducting business, subject to confidentiality agreements.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">5. Data Security</h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your
              personal information against unauthorized access, alteration, disclosure, or destruction.
              However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">6. Cookies and Tracking</h2>
            <p className="leading-relaxed">
              Our website may use cookies and similar tracking technologies to enhance your browsing
              experience. You can control cookie preferences through your browser settings. Disabling
              cookies may affect some features of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">7. Your Rights</h2>
            <p className="leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Withdraw consent for marketing communications</li>
              <li>Lodge a complaint with a data protection authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">8. Third-Party Links</h2>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the
              privacy practices of those websites. We encourage you to review their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">9. Children's Privacy</h2>
            <p className="leading-relaxed">
              Our website and services are not directed to individuals under the age of 18. We do not
              knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">10. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this
              page with an updated "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">11. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-3 p-4 bg-[rgba(45,80,22,0.05)] rounded-xl">
              <p className="font-semibold text-[rgb(0,55,32)]">{mockData.company.name}</p>
              <p>Phone: {mockData.company.phone}</p>
              <p>Email: {mockData.company.email}</p>
              <p>Service Area: Ottawa & Surrounding Areas</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[rgb(0,55,32)] text-white py-8 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center text-sm text-white/60">
          <p>&copy; 2026 {mockData.company.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
